// Blog post data — keep content in JS for now (no CMS).
// Each post supports headings/paragraphs/lists in a simple structured format.

export const BLOG_POSTS = [
  {
    slug: "how-much-do-movers-cost-casper-wy",
    title: "How Much Do Movers Cost in Casper, WY? A 2026 Guide",
    excerpt:
      "Local moving prices in Wyoming explained — what affects the cost, what's included, and how to spot hidden fees before you book.",
    image: "https://images.pexels.com/photos/4506270/pexels-photo-4506270.jpeg",
    date: "2026-04-12",
    readMin: 6,
    category: "Pricing",
    seoTitle: "How Much Do Movers Cost in Casper, WY? (2026 Guide) | Crosstown Movers",
    metaDesc:
      "What moving companies in Casper, WY actually cost in 2026. Hourly rates, long-distance pricing, hidden fees to avoid, and how to get a fair quote.",
    sections: [
      {
        type: "p",
        text: "If you're planning a move in Casper, WY — or anywhere in Wyoming — the first question on your mind is probably: 'how much is this going to cost?' Fair question. Moving prices feel mysterious, especially when every company quotes a little differently.",
      },
      {
        type: "p",
        text: "After 46 years moving Wyoming families and businesses, here's the honest breakdown of what local moves actually cost in 2026 — and what to look out for so you don't get burned.",
      },
      { type: "h2", text: "The short answer" },
      {
        type: "p",
        text: "A typical local move in Casper, WY in 2026 runs between $400 and $1,500 — depending on home size, crew size, and total hours. Long-distance and out-of-state Wyoming moves are quoted by weight and distance, usually $1,500 to $5,000+.",
      },
      { type: "h2", text: "What affects the price?" },
      {
        type: "list",
        items: [
          "Home size — studio vs. 4-bedroom is a big difference in hours",
          "Crew size — 2 movers vs. 3 or 4 (more crew = faster, not always cheaper)",
          "Distance between addresses",
          "Stairs, elevators, and tight access",
          "Heavy or specialty items (piano, safe, treadmill, refrigerator)",
          "Packing services (full, partial, or none)",
          "Day of week (weekends are usually busier)",
          "Time of year (May–September is peak season)",
        ],
      },
      { type: "h2", text: "Typical Wyoming local move pricing (2026)" },
      {
        type: "list",
        items: [
          "Studio / 1-bedroom apartment: $300–$600",
          "2-bedroom home: $500–$900",
          "3-bedroom home: $800–$1,300",
          "4+ bedroom home: $1,200–$2,000+",
          "Single heavy item (piano, safe): $150–$500",
          "Office / commercial moves: custom quote",
        ],
      },
      { type: "h2", text: "Hidden fees to watch for" },
      {
        type: "p",
        text: "The Wyoming moving industry is mostly honest, but a few practices to watch for:",
      },
      {
        type: "list",
        items: [
          "Fuel surcharges added at the end (we don't charge these)",
          "'Stairs fees' that weren't disclosed up front",
          "Travel time billed before they arrive at your home",
          "Box and tape fees that weren't itemized",
          "Higher hourly rates for evenings or weekends without warning",
        ],
      },
      { type: "h2", text: "How to get a fair quote" },
      {
        type: "list",
        items: [
          "Get at least 2–3 written quotes",
          "Ask for hourly rates AND minimum hours",
          "Ask whether equipment, pads, and floor protection are included",
          "Confirm fuel and travel charges are baked in",
          "Verify the company is licensed and insured",
        ],
      },
      { type: "h2", text: "The bottom line" },
      {
        type: "p",
        text: "A trustworthy mover in Casper, WY tells you the price up front — including everything. At Crosstown Movers we've been doing it that way since 1980. Want a free, no-obligation quote? Call (307) 265-8291 or use our Instant Quote tool — takes 60 seconds.",
      },
    ],
  },

  {
    slug: "moving-in-winter-wyoming-tips",
    title: "Moving in Winter: 7 Tips for a Smooth Wyoming Winter Move",
    excerpt:
      "Snow, ice, and short days don't have to derail your move. Here's how Wyoming's experienced movers prepare for winter relocations.",
    image: "https://images.pexels.com/photos/3933889/pexels-photo-3933889.jpeg",
    date: "2026-02-08",
    readMin: 5,
    category: "Tips",
    seoTitle: "Winter Moving Tips for Wyoming | Crosstown Movers",
    metaDesc:
      "Moving in winter in Wyoming? 7 expert tips from Casper movers — protect floors, plan for snow, schedule smart, and keep belongings safe.",
    sections: [
      {
        type: "p",
        text: "Wyoming winters are no joke. Snow, ice, sub-zero mornings, and short daylight hours can turn an ordinary move into a logistical nightmare — if you're not ready. After 46 years of winter moves in Casper, Mills, Cheyenne, and Laramie, here are the tips that make all the difference.",
      },
      { type: "h2", text: "1. Schedule the earliest start time" },
      {
        type: "p",
        text: "Winter daylight is short. Starting your move at 8 AM gives the crew a full day of light to load, drive, and unload before sunset. We recommend booking the first slot of the day in winter.",
      },
      { type: "h2", text: "2. Clear walkways and driveways" },
      {
        type: "p",
        text: "Shovel and salt 24 hours before the move. Movers carry heavy loads — slip-and-fall risks are real. The better the path, the faster (and safer) the move.",
      },
      { type: "h2", text: "3. Lay down extra floor protection" },
      {
        type: "p",
        text: "Snow + dirt + boots = ruined floors. We bring runner mats and door-jamb covers for every winter move, but be ready with old towels at exterior doorways.",
      },
      { type: "h2", text: "4. Protect cold-sensitive items" },
      {
        type: "list",
        items: [
          "Houseplants — wrap in plastic or move them in your own car with the heat on",
          "Electronics — don't unbox immediately at destination; let them warm up first",
          "Wines and liquids — temperature shock can ruin them",
          "Wooden furniture — sudden temperature changes can crack finishes",
        ],
      },
      { type: "h2", text: "5. Confirm utilities are on at both addresses" },
      {
        type: "p",
        text: "Heat needs to be on at the new house before you arrive. Setting boxes down in a freezing home is miserable, and pipes can freeze in vacant Wyoming homes faster than you'd think.",
      },
      { type: "h2", text: "6. Keep a 'first-night kit' separate" },
      {
        type: "p",
        text: "Heavy coats, blankets, gloves, hats, a phone charger, prescriptions, kids' essentials, and pet supplies. Don't bury these in moving boxes — keep them in your own car.",
      },
      { type: "h2", text: "7. Hire a Wyoming-based mover" },
      {
        type: "p",
        text: "Out-of-state crews don't know Wyoming weather. Local Casper movers — like our team — know how to plan around storms, drive on ice, and adjust on the fly when conditions change.",
      },
      {
        type: "p",
        text: "Need to plan a winter move? Call (307) 265-8291 — open 24 hours, 4.5★ from 60+ Wyoming customers.",
      },
    ],
  },

  {
    slug: "how-to-pack-kitchen-like-a-pro",
    title: "How to Pack a Kitchen Like a Pro Mover",
    excerpt:
      "The kitchen is the most-feared room to pack. Here's the step-by-step system Wyoming's professional packers use to do it fast and safely.",
    image: "https://images.pexels.com/photos/4393668/pexels-photo-4393668.jpeg",
    date: "2026-01-22",
    readMin: 7,
    category: "Packing",
    seoTitle: "How to Pack a Kitchen for a Move | Pro Wyoming Movers Guide",
    metaDesc:
      "Step-by-step pro guide to packing a kitchen — from glassware to small appliances. Tips from professional Wyoming movers with 46 years of experience.",
    sections: [
      {
        type: "p",
        text: "Ask any professional mover what the most-dreaded room to pack is, and the answer is the same: the kitchen. Glassware, plates, sharp knives, awkward appliances, that one drawer of mystery utensils — it's a logistical puzzle.",
      },
      {
        type: "p",
        text: "Here's the exact system our Wyoming packing crews use to pack a typical kitchen in 2–3 hours, with zero breakage.",
      },
      { type: "h2", text: "What you'll need" },
      {
        type: "list",
        items: [
          "Small boxes (for heavy items like dishes and canned goods)",
          "Medium boxes (for small appliances)",
          "Dish-pack boxes (double-walled, the gold standard)",
          "Packing paper (lots of it — way more than you think)",
          "Bubble wrap (for fragile or sentimental items)",
          "Permanent marker for labeling",
          "Painter's tape (to keep cabinet doors closed)",
        ],
      },
      { type: "h2", text: "Step 1: Empty the trash and clean as you go" },
      {
        type: "p",
        text: "Every kitchen has expired food, half-empty bottles, and forgotten Tupperware. Toss it now — don't pay to move it.",
      },
      { type: "h2", text: "Step 2: Pack items in this order" },
      {
        type: "list",
        items: [
          "Decor and rarely-used items first (top shelves, the back of cabinets)",
          "Small appliances next (toaster, blender, coffee grinder)",
          "Cookware and bakeware (pots, pans, sheet trays)",
          "Pantry food (canned goods in small boxes — heavy!)",
          "Glassware and plates (use dish-pack boxes!)",
          "Daily-use items last (mugs, the one knife you use, the kettle)",
        ],
      },
      { type: "h2", text: "Step 3: Wrap glasses and plates the pro way" },
      {
        type: "list",
        items: [
          "Plates: stack vertically (like records), with paper between every plate",
          "Glasses: stuff with crumpled paper, then wrap individually",
          "Stemware: bubble wrap each, place upside-down, fill all gaps",
          "Heavy items go on the bottom, lighter items on top — always",
        ],
      },
      { type: "h2", text: "Step 4: Box up the appliances" },
      {
        type: "p",
        text: "If you still have the original box (with foam inserts), use it. If not, wrap the appliance in pads or bubble wrap, place it in a medium box, and fill any gaps with paper. Tape the cord to the appliance so nothing dangles.",
      },
      { type: "h2", text: "Step 5: Knives and sharp items" },
      {
        type: "p",
        text: "Wrap each knife individually in paper, then bundle them with painter's tape. Label the bundle 'KNIVES' clearly. We've seen too many close calls — don't loose-pack sharp objects.",
      },
      { type: "h2", text: "Step 6: Label every box" },
      {
        type: "p",
        text: "On at least two sides, write: 'KITCHEN', what's inside, and 'FRAGILE' if applicable. Future-you on moving day will thank you.",
      },
      { type: "h2", text: "Don't want to pack at all?" },
      {
        type: "p",
        text: "We hear you. Crosstown Movers offers full and partial kitchen packing across Wyoming. Most kitchens get packed in 2–3 hours by our pro team. Free quotes — call (307) 265-8291.",
      },
    ],
  },

  {
    slug: "local-vs-long-distance-movers-wyoming",
    title: "Local vs. Long-Distance Movers in Wyoming: Which Do You Need?",
    excerpt:
      "Wyoming is a big state. Whether you're moving across town or across the state, here's how to decide which type of mover you actually need.",
    image: "https://images.pexels.com/photos/2882566/pexels-photo-2882566.jpeg",
    date: "2025-12-30",
    readMin: 5,
    category: "Guides",
    seoTitle: "Local vs Long-Distance Movers in Wyoming | Crosstown Movers Guide",
    metaDesc:
      "Local or long-distance movers in Wyoming — what's the difference, what's it cost, and which do you need? A practical guide from Casper-based pros.",
    sections: [
      {
        type: "p",
        text: "Wyoming is geographically huge. A move from Casper to Cheyenne is 180 miles — for context, that's longer than a typical 'cross-state' move in most other states. The line between 'local' and 'long-distance' isn't always obvious, and it affects how you should plan, budget, and book.",
      },
      { type: "h2", text: "What counts as 'local'?" },
      {
        type: "p",
        text: "In the moving industry, local generally means under 100 miles AND staying within the same state. Most moves within Casper, Mills, Bar Nunn, Evansville, and surrounding towns are 100% local. Local moves are billed by the hour.",
      },
      { type: "h2", text: "What counts as 'long-distance'?" },
      {
        type: "p",
        text: "Anything over 100 miles, or any move crossing state lines. Casper to Cheyenne, Casper to Riverton, or any Wyoming-to-Colorado/Montana/Idaho move falls into long-distance. These are billed by weight and distance, not by hour.",
      },
      { type: "h2", text: "Pricing differences" },
      {
        type: "list",
        items: [
          "Local: hourly rate × number of movers × hours (typically $400–$1,500 for a home)",
          "Long-distance: weight (in pounds) × distance + fuel + crew (typically $1,500–$5,000+)",
        ],
      },
      { type: "h2", text: "Booking lead time" },
      {
        type: "list",
        items: [
          "Local — 1–4 weeks ahead is typical, last-minute often possible",
          "Long-distance — 4–8 weeks ahead recommended (especially summer)",
        ],
      },
      { type: "h2", text: "Same crew or different crews?" },
      {
        type: "p",
        text: "At Crosstown Movers, the same Wyoming crew handles your move from start to finish — local or long-distance, no transfers, no warehouses, no 'driver' separate from your packing team. That's a big deal in long-distance moves where most national companies hand your stuff off to a third-party driver.",
      },
      { type: "h2", text: "Which do you need?" },
      {
        type: "list",
        items: [
          "Moving within Casper or to a nearby town like Mills/Bar Nunn → local",
          "Moving to another Wyoming city (Cheyenne, Laramie, Riverton, Lander) → long-distance",
          "Moving to or from another state → long-distance interstate",
        ],
      },
      {
        type: "p",
        text: "Either way, Crosstown Movers handles both with the same team, the same equipment, and the same up-front pricing. Call (307) 265-8291 for a free quote on whichever type of move you're planning.",
      },
    ],
  },
];

export const BLOG_CATEGORIES = ["All", "Pricing", "Tips", "Packing", "Guides"];
